import { useState, useEffect } from "react"
import { useSearchParams } from "react-router-dom"
import { Plus, Building2 } from "lucide-react"

import { APP_NAME } from "@/lib/appConfig"
import { useProperties } from "@/features/properties/hooks/useProperties"
import { PropertyCard } from "@/features/properties/components/PropertyCard"
import { PropertyForm } from "@/features/properties/components/PropertyForm"
import { PageHeader } from "@/components/shared/PageHeader"
import { EmptyState } from "@/components/shared/EmptyState"
import { ListSkeleton } from "@/components/shared/LoadingSkeleton"
import { PlanLimitBanner } from "@/components/shared/PlanLimitBanner"
import { PullToRefresh } from "@/components/shared/PullToRefresh"
import { Button } from "@/components/ui/button"
import { usePlan } from "@/hooks/usePlan"

export default function PropertiesPage() {
  const [searchParams] = useSearchParams()
  const [sheetOpen, setSheetOpen] = useState(() => searchParams.get("new") === "1")
  const { data: properties, isLoading, isError, refetch } = useProperties()
  const { checkLimit } = usePlan()

  useEffect(() => {
    const prev = document.title
    document.title = `Properties | ${APP_NAME}`
    return () => { document.title = prev }
  }, [])

  const count = properties?.length ?? 0
  const { allowed, limit } = checkLimit("properties", count)

  const stats = {
    total: properties?.length ?? 0,
    occupied: properties?.filter(p => ((p as any).active_leases_count ?? 0) > 0).length ?? 0,
    vacant: properties?.filter(p => ((p as any).active_leases_count ?? 0) === 0).length ?? 0,
  }

  return (
    <div className="flex flex-col h-full">
      <PageHeader
        title="Properties"
        action={
          <Button
            size="sm"
            className="h-9 rounded-xl gap-1"
            onClick={() => setSheetOpen(true)}
            disabled={!allowed}
          >
            <Plus className="h-4 w-4" />
            Add
          </Button>
        }
      />

      <div className="px-4 mb-5">
        <div className="flex bg-card/60 backdrop-blur-xl border border-border/50 rounded-2xl shadow-soft divide-x divide-border/50 overflow-hidden">
          <div className="flex-1 py-3 px-4 flex flex-col items-center justify-center text-center">
            <span className="text-xs font-bold text-muted-foreground uppercase tracking-widest mb-1">Total Properties</span>
            <span className="text-xl font-extrabold text-foreground">{stats.total}</span>
          </div>
          <div className="flex-1 py-3 px-4 flex flex-col items-center justify-center text-center bg-primary/5">
            <span className="text-xs font-bold text-primary/80 uppercase tracking-widest mb-1">Occupied</span>
            <span className="text-xl font-extrabold text-primary">{stats.occupied}</span>
          </div>
          <div className="flex-1 py-3 px-4 flex flex-col items-center justify-center text-center">
            <span className="text-xs font-bold text-muted-foreground uppercase tracking-widest mb-1">Vacant</span>
            <span className="text-xl font-extrabold text-foreground">{stats.vacant}</span>
          </div>
        </div>
      </div>

      <PullToRefresh onRefresh={async () => { await refetch() }} className="flex-1 pb-nav">
        {!allowed && (
          <div className="px-4 pt-4">
            <PlanLimitBanner
              feature="properties"
              limit={limit}
              current={count}
            />
          </div>
        )}

        {isLoading && <ListSkeleton count={4} />}

        {isError && (
          <div className="flex flex-col items-center py-16 px-8 text-center">
            <p className="text-sm text-muted-foreground mb-4">
              Failed to load properties.
            </p>
            <Button variant="outline" size="sm" onClick={() => refetch()}>
              Try again
            </Button>
          </div>
        )}

        {!isLoading && !isError && properties?.length === 0 && (
          <EmptyState
            icon={<Building2 className="h-9 w-9 text-muted-foreground" />}
            title="No properties yet"
            description="Add your first property to start managing your portfolio."
            action={{ label: "Add Property", onClick: () => setSheetOpen(true) }}
          />
        )}

        {!isLoading && !isError && properties && properties.length > 0 && (
          <div className="px-4 pt-4 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
            {properties.map((property) => (
              <PropertyCard key={property.id} property={property} />
            ))}
          </div>
        )}
      </PullToRefresh>

      <PropertyForm
        open={sheetOpen}
        onOpenChange={setSheetOpen}
        onSuccess={() => setSheetOpen(false)}
      />
    </div>
  )
}
